import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogActions, Button, TextField, Box, Typography, Chip, Divider, IconButton, CircularProgress, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { toPng } from 'html-to-image';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import staticTheme from '../theme';
import { useT } from '../i18n/LanguageContext';

// When printing, hide everything except the brief.
const PRINT_CSS = `@media print {
  body * { visibility: hidden !important; }
  #concept-brief, #concept-brief * { visibility: visible !important; }
  #concept-brief { position: absolute !important; left: 0; top: 0; width: 100%; box-shadow: none !important; border-radius: 0 !important; }
}`;

// Same small-caps section-title language as the Mentor chat's own
// .mentor-block__title (Chatbot.css) — muted, compact, not a big colored
// headline per section like the brief used to have.
function Section({ title, action, children }) {
  return (
    <Box sx={{ mb: 1.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography sx={{
          fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase',
          letterSpacing: '0.06em', color: '#5B6770',
        }}>{title}</Typography>
        {action}
      </Box>
      {children}
    </Box>
  );
}

function Empty({ children }) {
  return <Typography sx={{ color: staticTheme.home.inkFaint, fontSize: '0.82rem' }}>{children}</Typography>;
}

// Compact bullet list shared by cantiere/fondamenta/note — plain strings in,
// styled <li> out.
function BulletList({ items, color }) {
  if (!items || !items.length) return null;
  return (
    <ul style={{ margin: '2px 0 8px', paddingLeft: 16, color, fontSize: '0.82rem', lineHeight: 1.5 }}>
      {items.map((t, i) => <li key={i}>{t}</li>)}
    </ul>
  );
}

const getDecisionStatusStyle = (t) => ({
  confermata: { label: `✓ ${t('canvas.conceptBrief.decisionStatus.confermata')}`, color: '#1B7A4A', bg: 'rgba(27,122,74,0.08)' },
  scartata: { label: `✕ ${t('canvas.conceptBrief.decisionStatus.scartata')}`, color: '#8A8F94', bg: 'rgba(20,24,29,0.04)' },
  superata: { label: `→ ${t('canvas.conceptBrief.decisionStatus.superata')}`, color: '#8A8F94', bg: 'rgba(20,24,29,0.04)' },
  proposta: { label: `… ${t('canvas.conceptBrief.decisionStatus.proposta')}`, color: '#B58A2E', bg: 'rgba(181,138,46,0.1)' },
});

function DecisionEntry({ decision, byId, ink }) {
  const t = useT();
  const decisionStatusStyle = getDecisionStatusStyle(t);
  const style = decisionStatusStyle[decision.status] || decisionStatusStyle.proposta;
  const strike = decision.status === 'scartata' || decision.status === 'superata';
  const replacement = decision.supersededBy ? byId[decision.supersededBy] : null;
  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Chip
          label={style.label} size="small"
          sx={{ bgcolor: style.bg, color: style.color, fontSize: '0.66rem', fontWeight: 700, height: 20 }}
        />
        <Typography sx={{
          color: ink, fontSize: '0.82rem', fontWeight: 600,
          textDecoration: strike ? 'line-through' : 'none',
          opacity: strike ? 0.65 : 1,
        }}>
          {decision.text}
        </Typography>
        {decision.origine === 'mentor' && (
          <Chip label={t('canvas.conceptBrief.mentorChip')} size="small" sx={{ bgcolor: 'rgba(20,118,150,0.08)', fontSize: '0.6rem', height: 18 }} />
        )}
        {decision.carta && (
          <Chip label={decision.carta} size="small" sx={{ bgcolor: 'rgba(20,24,29,0.05)', fontSize: '0.6rem', height: 18 }} />
        )}
      </Box>
      {decision.motivazione && (
        <Typography sx={{ color: '#5B6770', fontSize: '0.74rem', mt: 0.25, ml: 0.25 }}>
          {decision.motivazione}
        </Typography>
      )}
      {replacement && (
        <Typography sx={{ color: '#5B6770', fontSize: '0.7rem', mt: 0.25, ml: 0.25, fontStyle: 'italic' }}>
          {t('canvas.conceptBrief.supersededBy', { text: replacement.text })}
        </Typography>
      )}
    </Box>
  );
}

// 1-page, branded, exportable concept brief compiled from the canvas.
export default function ConceptBrief({ open, onClose, brief, onRegenerateSummary, onTitleChange }) {
  const theme = useTheme();
  const t = useT();
  const [goal, setGoal] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState(false);

  useEffect(() => { if (open && brief) setGoal(brief.title || ''); }, [open, brief]);
  useEffect(() => { if (open) setRegenerateError(false); }, [open]);

  const handleRegenerate = async () => {
    if (!onRegenerateSummary || regenerating) return;
    setRegenerating(true);
    setRegenerateError(false);
    try {
      const data = await onRegenerateSummary();
      if (data?.obiettivo) setGoal(data.obiettivo);
    } catch (e) {
      setRegenerateError(true);
    } finally {
      setRegenerating(false);
    }
  };

  const ink = theme.home?.ink || '#14181D';
  const cyanText = theme.home?.cyanText || '#147696';
  const display = theme.home?.display || 'Georgia, serif';

  const summary = (brief && brief.summary) || '';
  const cards = (brief && brief.cards) || [];
  const notes = (brief && brief.notes) || [];
  const brainstorm = (brief && brief.brainstorm) || [];
  const cantiere = (brief && brief.cantiere) || { mappa: [], prossimi_passi: [] };
  const fondamenta = (brief && brief.fondamenta) || { vincoli: [], preferenze_metodo: [] };
  const hasCantiere = cantiere.mappa.length || cantiere.prossimi_passi.length;
  // No `vincitore` check: it moved to the decision log, and sessions persisted
  // before that still carry the old string — testing it here would make the
  // section render as an empty heading for those sessions.
  const hasFondamenta = fondamenta.vincoli.length || fondamenta.preferenze_metodo.length;
  const direzione = (brief && brief.direzione) || '';
  const evidenzeChiave = (brief && brief.evidenzeChiave) || [];
  const carteCollegate = (brief && brief.carteCollegate) || [];
  const decisions = (brief && brief.decisions) || [];

  // Group notes by tag (phase) when present; the rest go in a flat list.
  const tagged = {};
  const flat = [];
  notes.forEach((n) => {
    if (n.tag) {
      if (!tagged[n.tag]) tagged[n.tag] = [];
      tagged[n.tag].push(n.label);
    } else {
      flat.push(n.label);
    }
  });

  const downloadPng = async () => {
    const el = document.getElementById('concept-brief');
    if (!el) return;
    try {
      const dataUrl = await toPng(el, { backgroundColor: '#ffffff', pixelRatio: 2 });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = 'concept-brief.png';
      a.click();
    } catch (e) {
      console.error('PNG export failed', e);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <style>{PRINT_CSS}</style>
      <DialogContent sx={{ bgcolor: '#FBF8F4' }}>
        <TextField
          label={t('canvas.conceptBrief.goalFieldLabel')} value={goal}
          onChange={(e) => {
            const v = e.target.value;
            setGoal(v);
            // Local state alone doesn't persist anywhere — without this the
            // edit looked applied while the dialog stayed open, but
            // openConceptBrief rebuilds `title` fresh from sessionBrief on
            // every open, silently reverting it.
            if (onTitleChange) onTitleChange(v);
          }}
          fullWidth size="small" sx={{ mb: 1.5, '@media print': { display: 'none' } }}
        />

        <Box id="concept-brief" sx={{
          bgcolor: '#fff', p: { xs: 2.5, md: 3.5 }, borderRadius: '14px',
          border: '1px solid #EFE7DA', boxShadow: '0 16px 40px rgba(20,24,29,0.14)',
          maxWidth: 620, mx: 'auto',
        }}>
          <Typography sx={{ textTransform: 'uppercase', letterSpacing: '0.14em', fontSize: '0.62rem', color: cyanText, fontWeight: 700 }}>
            {t('canvas.conceptBrief.kicker')}
          </Typography>
          <Typography sx={{ fontFamily: display, fontWeight: 700, fontSize: { xs: '1.3rem', md: '1.5rem' }, letterSpacing: '-0.015em', color: ink, mt: 0.5 }}>
            {goal || t('canvas.conceptBrief.untitled')}
          </Typography>
          <Divider sx={{ my: 1.5, borderColor: '#EFE7DA' }} />

          {(summary || onRegenerateSummary) && (
            <Section
              title={t('canvas.conceptBrief.sectionOverview')}
              action={onRegenerateSummary && (
                <Tooltip title={t('canvas.conceptBrief.regenerateTooltip')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleRegenerate}
                      disabled={regenerating}
                      sx={{ color: cyanText, '@media print': { display: 'none' } }}
                      aria-label={t('canvas.conceptBrief.regenerateAria')}
                    >
                      {regenerating ? <CircularProgress size={14} sx={{ color: cyanText }} /> : <AutoAwesomeIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            >
              {summary ? (
                <Typography sx={{ color: ink, fontSize: '0.85rem', lineHeight: 1.55 }}>{summary}</Typography>
              ) : (
                <Empty>{t('canvas.conceptBrief.emptySummary')}</Empty>
              )}
              {regenerateError && (
                <Typography sx={{ color: '#B24A35', fontSize: '0.74rem', mt: 0.5 }}>
                  {t('canvas.conceptBrief.regenerateError')}
                </Typography>
              )}
            </Section>
          )}

          {!!hasFondamenta && (
            <Section title={`🏛️ ${t('canvas.conceptBrief.sectionFoundations')}`}>
              <BulletList items={fondamenta.vincoli} color={ink} />
              <BulletList items={fondamenta.preferenze_metodo} color={ink} />
            </Section>
          )}

          {decisions.length > 0 && (
            <Section title={`🧭 ${t('canvas.conceptBrief.sectionDecisions')}`}>
              {(() => {
                const byId = Object.fromEntries(decisions.map((d) => [d.id, d]));
                return decisions.map((d) => (
                  <DecisionEntry key={d.id} decision={d} byId={byId} ink={ink} />
                ));
              })()}
            </Section>
          )}

          {!!hasCantiere && (
            <Section title={`🕒 ${t('canvas.conceptBrief.sectionWorksite')}`}>
              <BulletList items={cantiere.mappa} color={ink} />
              <BulletList items={cantiere.prossimi_passi} color={ink} />
            </Section>
          )}

          <Section title={t('canvas.conceptBrief.sectionCards')}>
            {cards.length ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {cards.map((c, i) => (
                  <Chip key={i} label={c.label} size="small" sx={{ bgcolor: 'rgba(20,118,150,0.08)', color: ink, fontSize: '0.72rem' }} />
                ))}
              </Box>
            ) : (
              <Empty>{t('canvas.conceptBrief.emptyCards')}</Empty>
            )}
          </Section>

          <Section title={t('canvas.conceptBrief.sectionNotes')}>
            {Object.keys(tagged).map((tag) => (
              <Box key={tag} sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: theme.home.inkMuted }}>{tag}</Typography>
                <BulletList items={tagged[tag]} color={ink} />
              </Box>
            ))}
            <BulletList items={flat} color={ink} />
            {notes.length === 0 && <Empty>{t('canvas.conceptBrief.emptyNotes')}</Empty>}
          </Section>

          {brainstorm.length > 0 && (
            <Section title={t('canvas.conceptBrief.sectionBrainstorm')}>
              {brainstorm.map((e, i) => (
                <Box key={e.id || i} sx={{ display: 'flex', gap: 1.5, mb: 1, alignItems: 'flex-start' }}>
                  {e.card && (
                    <Box sx={{ flex: '0 0 72px', textAlign: 'center' }}>
                      {e.card.image && (
                        <img src={e.card.image} alt={e.card.label || ''} style={{ width: 72, height: 'auto', borderRadius: 5, display: 'block' }} />
                      )}
                      <Typography sx={{ fontSize: '0.64rem', color: theme.home.inkMuted, mt: 0.4 }}>{e.card.label}</Typography>
                    </Box>
                  )}
                  {/* html pre-sanitized at input (BrainstormPanel.sanitizeHtml) */}
                  <Box sx={{ flex: 1, color: ink, fontSize: '0.82rem', '& ul': { margin: '2px 0', paddingLeft: '16px' } }}
                    dangerouslySetInnerHTML={{ __html: e.html || '' }} />
                </Box>
              ))}
            </Section>
          )}

          {direzione && (
            <Section title={t('canvas.conceptBrief.sectionDirection')}>
              <Typography sx={{ color: ink, fontSize: '0.82rem', lineHeight: 1.55 }}>{direzione}</Typography>
            </Section>
          )}

          {evidenzeChiave.length > 0 && (
            <Section title={t('canvas.conceptBrief.sectionKeyEvidence')}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                {evidenzeChiave.map((e, i) => (
                  <Chip
                    key={i}
                    label={e.text}
                    size="small"
                    title={e.phase}
                    sx={{ bgcolor: 'rgba(20,24,29,0.05)', color: ink, fontSize: '0.72rem' }}
                  />
                ))}
              </Box>
            </Section>
          )}

          {carteCollegate.length > 0 && (
            <Section title={t('canvas.conceptBrief.sectionLinkedCards')}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                {carteCollegate.map((c, i) => (
                  <Chip
                    key={i}
                    label={c.label}
                    size="small"
                    title={c.perche || (c.by === 'mentor' ? t('canvas.conceptBrief.cardSuggestedByMentor') : t('canvas.conceptBrief.cardLinkedByUser'))}
                    sx={{
                      bgcolor: c.by === 'mentor' ? 'rgba(20,118,150,0.08)' : 'rgba(20,24,29,0.05)',
                      color: ink, fontSize: '0.72rem',
                    }}
                  />
                ))}
              </Box>
            </Section>
          )}

          <Divider sx={{ my: 1.5, borderColor: '#EFE7DA' }} />
          <Typography sx={{ fontSize: '0.68rem', color: theme.home.inkFaint }}>
            {t('canvas.conceptBrief.footer')}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ '@media print': { display: 'none' } }}>
        <Button onClick={onClose} sx={{ color: theme.home.inkMuted }}>{t('canvas.conceptBrief.close')}</Button>
        <Button onClick={downloadPng} variant="outlined" sx={{ borderColor: cyanText, color: cyanText }}>{t('canvas.conceptBrief.downloadPng')}</Button>
        <Button onClick={() => window.print()} variant="contained" sx={{ bgcolor: cyanText, '&:hover': { bgcolor: '#17a3d1' } }}>
          {t('canvas.conceptBrief.printSave')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
